import { useEffect, useState } from 'react';
import { prefersReducedMotion } from '@/components/layout/shell/entrance';
import { cn } from '@/components/ui/cn';
import { KanameMark } from './KanameMark';
import { LOCK_DIM_AT, MARK_STATES, statePetals, stateReveal, type MarkStateKind } from './markStates';
import { FAN_OFF } from './petals';

/*
  StateMark — знак-состояние (R42): тонкая обёртка над KanameMark по словарю MARK_STATES.
  - medallion — во вкладке: тёмный круг (bg-rail) со знаком 64 px, вместо ядра кольцо — тот же вид, что у
    знака в меню, когда ядро ушло в выделение. Живое коралловое ядро на экране одно (R41), и оно в меню.
  - scene — на тёмной сцене, где оболочки ещё нет или уже нет (первая загрузка, падение интерфейса):
    знак 160 px с живым ядром — оно несёт тон, ритм и тревогу.
  Появление: «не найдено» и «нет доступа» раскрываются из сложенного веера («пропущенный такт»;
  пластина выходит и гаснет), остальные встают сразу — смена состояния идёт переходом самого знака.
  Знак декоративный: смысл несут текст рядом и роль страницы.
*/

type Pose = 'folded' | 'via' | 'final';

interface StateMarkProps {
  kind: MarkStateKind;
  /** Сколько шагов пройдено и сколько всего (сборка, сбой и нет связи стоят на достигнутом). */
  done?: number;
  total?: number;
  setting?: 'medallion' | 'scene';
  /** Ядро сцены: живое (по умолчанию) или кольцо. В медальоне — всегда кольцо. */
  core?: 'ring' | 'live';
  /** Тревога: каждое новое значение — два удара ядра и кольцо (у кольца — только кольцо). */
  alarm?: number;
  /** Сцена: размер знака (по умолчанию size-40); медальон: раскладка круга. */
  className?: string;
}

export function StateMark({
  kind,
  done = 0,
  total = 0,
  setting = 'medallion',
  core = 'live',
  alarm = 0,
  className,
}: StateMarkProps) {
  const state = MARK_STATES[kind];
  const live = setting === 'scene' && core === 'live';
  const reduced = prefersReducedMotion();

  // Поза появления: сложен → (весь веер) → поза состояния. Смена состояния на ходу не складывает веер.
  const [pose, setPose] = useState<Pose>(() => (state.bloom && !reduced ? 'folded' : 'final'));
  const [shownKind, setShownKind] = useState(kind);
  if (shownKind !== kind) {
    setShownKind(kind);
    setPose(state.via && !reduced ? 'via' : 'final');
  }
  useEffect(() => {
    if (pose === 'folded') {
      // Два кадра: сложенная поза успевает встать, и веер раскрывается переходом, а не появляется.
      let raf = requestAnimationFrame(() => {
        raf = requestAnimationFrame(() => setPose(state.via ? 'via' : 'final'));
      });
      return () => cancelAnimationFrame(raf);
    }
    if (pose === 'via') {
      const t = window.setTimeout(() => setPose('final'), LOCK_DIM_AT);
      return () => window.clearTimeout(t);
    }
    return undefined;
  }, [pose, state.via]);

  // Живое ядро, входя в состояние с тревогой, даёт одну тревогу — и при появлении (падение), и при смене
  // (сбой на шаге). Кадром позже: знак уже стоит, а отклик играет только на новое значение (R23).
  const [entered, setEntered] = useState(0);
  const alarms = live && state.alarm;
  useEffect(() => {
    if (!alarms) return undefined;
    const t = window.setTimeout(() => setEntered((n) => n + 1), 0);
    return () => window.clearTimeout(t);
  }, [alarms, kind]);

  const petals =
    pose === 'folded' ? FAN_OFF : pose === 'via' && state.via ? state.via : statePetals(kind, done, total);
  const mark = (
    <KanameMark
      petals={petals}
      reveal={stateReveal(kind, done, total)}
      tone={state.tone}
      rhythm={live ? state.rhythm : 'calm'}
      breathing={live}
      core={live ? 'home' : 'away'}
      cue={{ gesture: 'alarm', at: entered + alarm }}
      className={setting === 'scene' ? cn('pointer-events-none size-40', className) : 'size-16'}
    />
  );
  if (setting === 'scene') return mark;
  return (
    <span
      aria-hidden
      data-slot="state-mark"
      className={cn(
        'pointer-events-none grid size-24 shrink-0 place-items-center rounded-full bg-rail ring-1 ring-line',
        className,
      )}
    >
      {mark}
    </span>
  );
}
